/**
 * Usage telemetry to hs-telemetry (hs-pmo/ep20#1, task:ff0821866c7a).
 *
 * Organisational, never personal: nothing here knows who the person is. The SDK
 * (public/hs-telemetry.js, vendored from hs-telemetry/sdk) loads only when
 * /api/telemetry/config says it is enabled, and until then every call is a no-op.
 * Telemetry must never break or slow the page it measures, so every path here
 * swallows its own failures.
 */
import { getValidAccessToken } from './auth';

type Client = {
  feature: (id: string, extra?: Record<string, unknown>) => void;
  mark: (metric: string, ms: number) => void;
};

let client: Client | null = null;
let booting: Promise<void> | null = null;

/** The organisation picked in the rail, for the relay to attribute batches to. A beacon
 *  carries cookies to its own origin and nothing else, so it travels as a cookie; the
 *  relay uses it only if the person belongs to that organisation. */
export function setTelemetryOrg(orgId: string | null): void {
  try {
    document.cookie = orgId
      ? `pmo_telemetry_org=${encodeURIComponent(orgId)}; path=/api/telemetry; SameSite=Strict; Secure`
      : 'pmo_telemetry_org=; path=/api/telemetry; Max-Age=0; SameSite=Strict; Secure';
  } catch { /* telemetry never breaks the page */ }
}

export function bootTelemetry(): Promise<void> {
  if (!booting) {
    booting = (async () => {
      try {
        const token = await getValidAccessToken();
        const res = await fetch('/api/telemetry/config', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const cfg = res.ok ? await res.json() : null;
        if (!cfg?.enabled || !cfg.endpoint) return;
        const sdkUrl = '/hs-telemetry.js';
        const sdk = await import(/* @vite-ignore */ sdkUrl);
        client = sdk.init({ appId: cfg.app_id || 'pmo', endpoint: cfg.endpoint, features: cfg.features || [] });
        if (pending) { client!.feature(pending); pending = null; }
      } catch { /* dormant */ }
    })();
  }
  return booting;
}

let pending: string | null = null;

/** A feature was used: on route entry for a view, on success for an action. */
export function feature(id: string | null | undefined): void {
  if (!id) return;
  try {
    if (client) client.feature(id);
    else pending = id;           // the first view, before the SDK has loaded
  } catch { /* never breaks the page */ }
}

export function mark(metric: string, ms: number): void {
  try { client?.mark(metric, ms); } catch { /* never breaks the page */ }
}

// Views, by route (PO-82's registry, hs-pmo/ep20#3). First match wins.
const VIEWS: [RegExp, string][] = [
  [/^\/$/, 'home.dashboard'],
  [/^\/(project\/[^/]+\/)?board$/, 'workitems.board'],
  [/^\/(project\/[^/]+\/)?list$/, 'workitems.list'],
  [/^\/(project\/[^/]+\/)?gantt$/, 'workitems.gantt'],
  [/^\/priorities$/, 'workitems.priorities'],
  [/^\/tasks$/, 'tasks.mine'],
  [/^\/portfolio$/, 'portfolio.view'],
  [/^\/(project\/[^/]+\/)?ideation$/, 'ideation.board'],
  [/^\/releases$/, 'releases.list'],
  [/^\/release-notes(\/[^/]+)?$/, 'releases.notes'],
  [/^\/funding$/, 'funding.list'],
  [/^\/funding\/[^/]+$/, 'funding.detail'],
  [/^\/funders$/, 'funders.list'],
  [/^\/funder\/[^/]+$/, 'funders.detail'],
  [/^\/crm\/leads$/, 'crm.leads'],
  [/^\/crm\/opportunities$/, 'crm.opportunities'],
  [/^\/crm\/clients$/, 'crm.clients'],
  [/^\/crm\/catalog$/, 'crm.catalog'],
  [/^\/crm\/quotes$/, 'crm.quotes'],
  [/^\/crm\/invoices$/, 'crm.invoices'],
  [/^\/crm\/contracts$/, 'crm.contracts'],
  [/^\/itsm$/, 'itsm.view'],
  [/^\/cmdb$/, 'cmdb.view'],
  [/^\/training$/, 'training.programmes'],
  [/^\/training\/new$/, 'training.create'],
  [/^\/training\/[^/]+\/decisions$/, 'training.decisions'],
  [/^\/training\/[^/]+\/campaigns\//, 'training.campaigns'],
  [/^\/training\/[^/]+\/work\//, 'training.work'],
  [/^\/training\/[^/]+\/profile$/, 'training.profile'],
  [/^\/training\/[^/]+\/access$/, 'training.access'],
  [/^\/training\/[^/]+\/relationships$/, 'training.relationships'],
  [/^\/training\/[^/]+\/[^/]+$/, 'training.boards'],
  [/^\/training\/[^/]+$/, 'training.programme'],
  [/^\/approvals$/, 'approvals.inbox'],
  [/^\/ways\/design-standard$/, 'ways.design-standard'],
  [/^\/ways$/, 'ways.inbox'],
  [/^\/admin\/users$/, 'admin.users'],
  [/^\/admin\/api-tokens$/, 'admin.api-tokens'],
  [/^\/admin\/access$/, 'admin.access'],
  [/^\/admin\/resources$/, 'admin.resources'],
  [/^\/admin\/outbox$/, 'admin.outbox'],
  [/^\/admin\/workflows$/, 'admin.workflows'],
  [/^\/admin\/organizations$/, 'admin.organizations'],
  [/^\/admin\/program-increments$/, 'admin.program-increments'],
  [/^\/settings$/, 'settings'],
  [/^\/item\/[^/]+$/, 'workitems.open'],
];

// Paths that are app pages, not a work item path under /:projectRef/*.
const APP_ROOTS = /^\/(project|admin|crm|training|ways|funding|funder|funders|release-notes|login|auth)(\/|$)/;

export function featureForPath(pathname: string): string | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  for (const [re, id] of VIEWS) if (re.test(path)) return id;
  // /:projectRef/* resolves a work item by its path (WorkItemPathResolver).
  if (/^\/[^/]+\/.+/.test(path) && !APP_ROOTS.test(path)) return 'workitems.open';
  return null;
}

// Actions, by the API call that succeeded. Keyed on method + path (BASE_URL stripped).
const ACTIONS: [string, RegExp, string][] = [
  ['POST', /^\/workitems$/, 'workitems.create'],
  ['PATCH', /^\/workitems\/[^/]+\/status$/, 'workitems.transition'],
  ['POST', /^\/workitems\/[^/]+\/comments$/, 'workitems.comment'],
  ['POST', /^\/projects$/, 'projects.create'],
  ['POST', /^\/sprints\/[^/]+\/rollover$/, 'sprint.rollover'],
  ['POST', /^\/approvals\/[^/]+\/decide$/, 'approvals.decide'],
];

export function featureForCall(method: string | undefined, endpoint: string, body?: unknown): string | null {
  const m = (method || 'GET').toUpperCase();
  const path = endpoint.split('?')[0];
  for (const [want, re, id] of ACTIONS) {
    if (want !== m || !re.test(path)) continue;
    // An idea is posted to /workitems too, but it is Ideation, not a work item.
    if (id === 'workitems.create' && isIdea(body)) return 'ideation.submit';
    return id;
  }
  return null;
}

function isIdea(body: unknown): boolean {
  if (typeof body !== 'string') return false;
  try { return JSON.parse(body)?.workItemType === 'idea'; } catch { return false; }
}
